import * as React from 'react';
import Text from 'components/commons/Text';
import { Row } from 'saladsoft-primitive';
import { InnerSection } from 'components/layouts/CommonForm';
import { InnerGroup } from 'components/layouts/CommonForm';
import { Button } from 'saladsoft-primitive';
import { useParams } from 'react-router-dom';
import { useStoreWrapper } from 'stores/StoreHelper';
import { useEffect, useState } from 'react';
import ServiceHelper from 'stores/ServiceHelper';
import { useMultiSelect } from 'components/multiSelectContainer/useMultiSelect';
import ItemList from 'components/multiSelectContainer/ItemList';
import ItemBox from 'components/multiSelectContainer/ItemBox';
import { ItemType } from 'components/multiSelectContainer/types';
import { toast } from 'react-toastify';
import { useStateContext } from 'providers/usePageState';
import styled from 'styled-components';

const AdditionalOptionContainer = () => {
  const { listingId } = useParams();
  const { uiStore } = useStoreWrapper();

  const { broadcast } = useStateContext();

  const [options, setOptions] = useState<ItemType[]>([]);

  const {
    selectedItems,
    handleRemoveItems,
    handleSelectItem,
    handleClickAll,
    covertModelToItem,
    unmount,
  } = useMultiSelect<ItemType & { id: number }>(
    options.filter((o): o is ItemType & { id: number } => o.id !== undefined),
  );

  const handleAddOptions = async () => {
    const filteredOptions = await fetchAllOptions();

    uiStore?.modal.show({
      title: '옵션상품 추가하기',
      style: { width: '600px', maxHeight: '80%', height: 'auto' },
      children: (
        <ItemList
          items={filteredOptions}
          onSaveItems={(items) => {
            const mergedItems = [...items, ...options];
            handleSave(mergedItems.map((o) => o.id ?? 0));
          }}
        />
      ),
      onConfirmed: () => {
        fetchListingOptions();
      },
    });
  };

  const handleSave = (items: number[]) => {
    ServiceHelper?.adminListingService?.saveOptions(Number(listingId), items).then((res) => {
      if (res.status < 300) {
        uiStore?.modal.confirm();
        toast.success('선택된 항목을 저장하였습니다.');
        fetchListingOptions();
        unmount();
        broadcast({ id: 'reload', param: 'option' });
      } else {
        toast.error('선택된 항목 저장에 실패하였습니다.');
      }
    });
  };

  /**
   * @description 시설에 포함된 주변 명소 취득
   */
  const fetchListingOptions = () => {
    ServiceHelper.adminListingService?.getOptionsByListing1(Number(listingId)).then((res) => {
      const items = res.data.map((option) => covertModelToItem(option));
      setOptions(items);
      broadcast({ id: 'reload', param: 'option' });
    });
  };

  /**
   * @description 전체 옵션 취득
   */
  const fetchAllOptions = async () => {
    const result = await ServiceHelper.adminOptionService?.getOptions();
    const filteredOptions = result?.data
      .map((option) => covertModelToItem(option))
      .filter((option) => !options?.some((checked) => checked.id === option.id));
    return filteredOptions as ItemType[];
  };

  useEffect(() => {
    if (listingId) {
      fetchListingOptions();
    }
  }, [listingId]);

  return (
    <div>
      <InnerSection>
        <InnerGroup style={{ minHeight: 'unset' }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              옵션상품 설정
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION" style={{ marginBottom: 10 }}>
              하단의 추가하기 버튼을 클릭하여 추가할 옵션을 지정 해 주세요.
            </Text>
            <Button type="outlined" style={{ width: 150 }} onClick={() => handleAddOptions()}>
              추가하기
            </Button>
          </div>
          <div style={{ width: '100%', flex: 1 }}>
            <>
              {options.length > 0 && (
                <Row marginBottom="20px" justifyContent="flex-start" gap="10px">
                  <Button type="outlined" style={{ width: 150 }} onClick={() => handleClickAll()}>
                    {selectedItems.length === options.length ? '전체 선택해제' : '전체선택'}
                  </Button>
                  <>
                    {selectedItems && selectedItems.length > 0 && (
                      <Button
                        type="outlined"
                        style={{ width: 150 }}
                        onClick={() => {
                          const ids = handleRemoveItems();
                          uiStore?.confirm.show({
                            title: '정말 제거하시겠습니까?',
                            onConfirmed: () => {
                              handleSave(ids);
                            },
                          });
                        }}
                      >
                        선택항목 제거하기
                      </Button>
                    )}
                  </>
                </Row>
              )}
            </>
            <Row flexWrap="wrap" width="100%" justifyContent="flex-start" gap="10px">
              {options?.map((option, index) => (
                <ItemBoxContainer key={option.id}>
                  <ItemBox
                    item={option}
                    onSelect={(item, checked) => {
                      handleSelectItem(item as ItemType & { id: number }, checked);
                    }}
                    checked={selectedItems.some((selected) => selected.id === option.id)}
                  />
                  {!option.isActive && (
                    <>
                      <InActiveWrapper />
                      <InActiveContent>비활성화됨</InActiveContent>
                    </>
                  )}
                </ItemBoxContainer>
              ))}
            </Row>
          </div>
        </InnerGroup>
      </InnerSection>
    </div>
  );
};

const ItemBoxContainer = styled.div`
  position: relative;
`;

const InActiveWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.5);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
`;

const InActiveContent = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  background-color: var(--point-color);
  border-radius: 10px;
  color: white;
  font-size: 12px;
  padding: 5px 10px;
  pointer-events: auto;
`;

export default AdditionalOptionContainer;
